<section class="home w-full max-w-6xl mx-auto px-3 md:px-6 py-6 space-y-8" dir="rtl">

  <!-- ── Hero greeting ─────────────────────────────────────────────── -->
  <div class="card-2026 card-2026--elevated flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-gradient-to-bl from-primary-50 to-surface">
    <div>
      <p class="text-base text-ink-500 font-medium">{{ welcome }}</p>
      <h1 class="text-3xl md:text-4xl font-bold text-primary-700 mt-1">
        <ng-container *ngIf="user; else greetingFallback">
          {{ user.firstName }} {{ user.lastName }}
        </ng-container>
        <ng-template #greetingFallback>
          <span class="skeleton-2026 inline-block h-9 w-48"></span>
        </ng-template>
      </h1>
      <p class="text-base text-ink-500 mt-2">
        {{ dateVal | date: "EEEE, d בMMMM y" : '' : 'he-IL' }}
      </p>
    </div>

    <a routerLink="/prayers/order/add"
       class="btn-2026--primary w-full md:w-auto md:min-w-[220px] shadow-card"
       aria-label="הזנת הזמנה חדשה">
      <span class="material-symbols-outlined" aria-hidden="true">add_circle</span>
      <span>הזמנה חדשה</span>
    </a>
  </div>

  <!-- ── Stats ─────────────────────────────────────────────────────── -->
  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">

    <!-- Total prayers / users -->
    <div class="stat-2026">
      <div class="stat-2026__label">סה"כ מתפללים</div>
      <ng-container *ngIf="usersLoaded; else usersLoadingTpl">
        <div class="stat-2026__value">{{ totalUsers }}</div>
        <div class="stat-2026__desc">משתמשים פעילים במערכת</div>
      </ng-container>
    </div>

    <!-- Synagogue balance -->
    <div class="stat-2026">
      <div class="stat-2026__label">יתרת בית הכנסת</div>
      <ng-container *ngIf="balanceDisplay !== null; else balanceLoadingTpl">
        <div class="stat-2026__value money"
             [class.money--credit]="balanceDisplay > 0"
             [class.money--debt]="balanceDisplay < 0"
             [class.money--zero]="balanceDisplay === 0">
          {{ balanceDisplay | currency : 'ILS' : 'symbol' : '1.0-2' }}
        </div>
        <!-- Newest transaction, so the admin can see it's included. -->
        <div class="stat-2026__desc"
             [class.text-success]="lastTxnAmount > 0"
             [class.text-danger]="lastTxnAmount < 0">
          <ng-container *ngIf="lastTxnAmount !== 0; else noTxnTpl">
            <span aria-hidden="true">{{ lastTxnAmount > 0 ? '↗︎' : '↘︎' }}</span>
            תנועה אחרונה:
            <span class="money">{{ lastTxnAmount > 0 ? '+' : '' }}{{ lastTxnAmount | currency : 'ILS' : 'symbol' : '1.0-2' }}</span>
          </ng-container>
          <ng-template #noTxnTpl>אין תנועות עדיין</ng-template>
        </div>
      </ng-container>
      <ng-template #balanceLoadingTpl>
        <ng-container *ngIf="!balanceError; else statErrorTpl">
          <div class="skeleton-2026 h-10 w-32 mt-1"></div>
          <div class="skeleton-2026 h-4 w-28 mt-3"></div>
        </ng-container>
      </ng-template>
    </div>

    <!-- Debtors -->
    <div class="stat-2026">
      <div class="stat-2026__label">מתפללים בחובה</div>
      <ng-container *ngIf="usersLoaded; else usersLoadingTpl">
        <!-- No `.money` on the count: its `unicode-bidi: plaintext` turns a
             bare number (no Hebrew letters) left-to-right and pushes it to
             the left edge of the card. -->
        <div class="stat-2026__value tabular-nums"
             [class.text-danger]="negativeBalanceCount > 0">
          {{ negativeBalanceCount }}
        </div>
        <div class="stat-2026__desc">
          חוב כולל:
          <span class="money"
                [class.money--debt]="totalDebt < 0"
                [class.money--zero]="totalDebt === 0">{{ totalDebt | currency : 'ILS' : 'symbol' : '1.0-0' }}</span>
        </div>
      </ng-container>
    </div>

    <!-- Shared loading / error states for the user-based cards. -->
    <ng-template #usersLoadingTpl>
      <ng-container *ngIf="!usersError; else statErrorTpl">
        <div class="skeleton-2026 h-10 w-24 mt-1"></div>
        <div class="skeleton-2026 h-4 w-40 mt-3"></div>
      </ng-container>
    </ng-template>

    <ng-template #statErrorTpl>
      <div class="stat-2026__desc text-danger mt-2">
        לא ניתן לטעון כרגע. נסו לרענן את הדף.
      </div>
    </ng-template>

  </div>

  <!-- ── Quick actions ─────────────────────────────────────────────── -->
  <section aria-labelledby="quick-actions-heading">
    <h2 id="quick-actions-heading" class="text-xl font-semibold text-ink-800 mb-3 px-1">
      {{ quickActionsHeader }}
    </h2>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
      <a *ngFor="let label of quickActions"
         [routerLink]="label.link"
         class="card-2026 flex flex-row items-center gap-4 group hover:bg-primary-50 transition-colors min-h-tap"
         [attr.aria-label]="label.txt">
        <span class="flex items-center justify-center w-12 h-12 rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-colors">
          <i class="material-icons text-2xl" aria-hidden="true">{{ label.icon }}</i>
        </span>
        <span class="text-lg font-semibold text-ink-800">{{ label.txt }}</span>
      </a>
    </div>
  </section>

  <!-- ── All actions (by department) ───────────────────────────────── -->
  <section aria-labelledby="all-actions-heading">
    <h2 id="all-actions-heading" class="text-xl font-semibold text-ink-800 mb-3 px-1">
      {{ allActionsHeader }}
    </h2>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
      <a *ngFor="let label of actions"
         [routerLink]="label.link"
         class="card-2026 flex flex-row items-center gap-4 group hover:bg-accent-50 transition-colors min-h-tap"
         [attr.aria-label]="label.txt">
        <span class="flex items-center justify-center w-12 h-12 rounded-xl bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-white transition-colors">
          <i class="material-icons text-2xl" aria-hidden="true">{{ label.icon }}</i>
        </span>
        <span class="text-lg font-semibold text-ink-800">{{ label.txt }}</span>
      </a>
    </div>
  </section>

</section>
